import React, { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import ArrowLeft from 'assets/salesNew/left.svg';
import ArrowRight from 'assets/salesNew/right.svg';
import Button from 'common-ui/button';
import Loading from 'common-ui/loading';
import { checkAddress, getLeadById } from 'features/newsales/newsalesActions';
import { notifyError } from 'utils/notifyConfig';

import { LeadFormProvider, useLeadForm } from './LeadFormContext';
import { ButtonIcon, ButtonSection, Responsive } from './SalesNew.styles';
import AttachTicketModal from './attachTicketModal';
import CompetitorInfo from './competitorInfo';
import CompetitorModal from './competitorModal';
import Header from './components/Header';
import ContactInfo from './contactInfo';
import ContactInfoB2B from './contactInfoB2B';
import MainInfo from './mainInfo';
import OfferInfo from './offerInfo';
import PassportInfo from './passportInfo';
import ProductInfo from './productInfo';
import SourceInfo from './sourceInfo';

const SalesNewContent = ({ isEditMode, leadId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { formData } = useLeadForm();
  const [isCompetitorModalOpen, setIsCompetitorModalOpen] = useState(false);
  const [isAttachTicketModalOpen, setIsAttachTicketModalOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [customerType, setCustomerType] = useState(formData.customerType || 'B2C');

  const [isCheckingAddress, setIsCheckingAddress] = useState(false);
  const [isMainInfoValid, setIsMainInfoValid] = useState(false);
  const [isContactInfoValid, setIsContactInfoValid] = useState(false);
  const [isSourceInfoValid, setIsSourceInfoValid] = useState(false);
  const [isProductInfoValid, setIsProductInfoValid] = useState(false);
  const [isOfferInfoValid, setIsOfferInfoValid] = useState(false);
  const [shouldValidateStep, setShouldValidateStep] = useState({
    0: false,
    1: false,
    2: false,
    3: false,
    4: false,
    5: false,
  });

  const handleNext = async () => {
    let canProceed = false;

    switch (activeStep) {
      case 0:
        setShouldValidateStep({ ...shouldValidateStep, 0: true });
        if (!isMainInfoValid) {
          return;
        }
        if (isEditMode) {
          setActiveStep((prev) => prev + 1);
        } else {
          setIsCheckingAddress(true);
          try {
            const result = await dispatch(
              checkAddress({
                countryId: formData.countryId,
                districtId: formData.districtId,
                cityId: formData.cityId,
                administrativeRegionId: formData.administrativeRegionId,
                streetId: formData.streetId,
                buildingHouseId: formData.buildingHouseId,
                apt: formData.apt || null,
              })
            ).unwrap();
            if (result?.valid) {
              setActiveStep((prev) => prev + 1);
            }
          } catch (error) {
            notifyError(error || 'This address belongs to another user');
          } finally {
            setIsCheckingAddress(false);
          }
        }
        return;
      case 1:
        setShouldValidateStep({ ...shouldValidateStep, 1: true });
        canProceed = isContactInfoValid;
        break;
      case 2:
        // passport step — all optional, always proceed
        canProceed = true;
        break;
      case 3:
        setShouldValidateStep({ ...shouldValidateStep, 3: true });
        canProceed = isSourceInfoValid;
        break;
      case 4:
        setShouldValidateStep({ ...shouldValidateStep, 4: true });
        canProceed = isProductInfoValid;
        break;
      case 5:
        setShouldValidateStep({ ...shouldValidateStep, 5: true });
        canProceed = isOfferInfoValid;
        break;
      case 6:
        canProceed = true;
        break;
      default:
        canProceed = false;
    }

    if (!canProceed) {
      return;
    }

    if (activeStep < 6) {
      setActiveStep((prev) => prev + 1);
    } else {
      // Step 6: Competitor selection
      // If competitor is selected, open modal for additional details
      // If no competitor selected, create lead directly
      if (formData.competitorId) {
        setIsCompetitorModalOpen(true);
      } else {
        setIsAttachTicketModalOpen(true);
      }
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const renderCurrentStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <MainInfo
            selectedType={customerType}
            setSelectedType={setCustomerType}
            onValidationChange={setIsMainInfoValid}
            shouldValidate={shouldValidateStep[0]}
          />
        );
      case 1:
        return customerType === 'B2B' ? (
          <ContactInfoB2B
            onValidationChange={setIsContactInfoValid}
            shouldValidate={shouldValidateStep[1]}
          />
        ) : (
          <ContactInfo
            onValidationChange={setIsContactInfoValid}
            shouldValidate={shouldValidateStep[1]}
          />
        );
      case 2:
        return <PassportInfo onValidationChange={() => {}} />;
      case 3:
        return (
          <SourceInfo
            onValidationChange={setIsSourceInfoValid}
            shouldValidate={shouldValidateStep[3]}
          />
        );
      case 4:
        return (
          <ProductInfo
            onValidationChange={setIsProductInfoValid}
            shouldValidate={shouldValidateStep[4]}
          />
        );
      case 5:
        return (
          <OfferInfo
            onValidationChange={setIsOfferInfoValid}
            shouldValidate={shouldValidateStep[5]}
          />
        );
      case 6:
        return <CompetitorInfo />;
      default:
        return <MainInfo />;
    }
  };
  return (
    <Responsive>
      <Header activeStep={activeStep > 2 ? activeStep - 2 : 0} />
      <div className="responsive-div">
        {renderCurrentStep()}
        <CompetitorModal
          isOpen={isCompetitorModalOpen}
          onClose={() => setIsCompetitorModalOpen(false)}
          onNext={() => {
            setIsCompetitorModalOpen(false);
            setIsAttachTicketModalOpen(true);
          }}
          isEditMode={isEditMode}
          leadId={leadId}
        />
        <AttachTicketModal
          isOpen={isAttachTicketModalOpen}
          onClose={() => setIsAttachTicketModalOpen(false)}
          onBack={() => {
            setIsAttachTicketModalOpen(false);
            if (formData.competitorId) setIsCompetitorModalOpen(true);
          }}
          isEditMode={isEditMode}
          leadId={leadId}
        />
        <ButtonSection>
          <Button className="button-back" onClick={handleBack}>
            <ButtonIcon src={ArrowLeft} />
            Back
          </Button>

          <Button className="button-next" onClick={handleNext} loading={isCheckingAddress}>
            {activeStep === 2 || activeStep === 6 ? 'Next/skip' : 'Next'}
            <ButtonIcon src={ArrowRight} />
          </Button>
        </ButtonSection>
      </div>
    </Responsive>
  );
};

const SalesNew = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [initialData, setInitialData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const isEditMode = searchParams.get('editMode') === 'true';
  const leadId = searchParams.get('leadId');

  useEffect(() => {
    const fetchLeadData = async () => {
      if (isEditMode && leadId) {
        setIsLoading(true);
        try {
          const response = await dispatch(getLeadById(leadId)).unwrap();
          setInitialData(response);
        } catch (error) {
          console.error('Failed to fetch lead data:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchLeadData();
  }, [isEditMode, leadId, dispatch]);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <LeadFormProvider initialData={initialData}>
      <SalesNewContent isEditMode={isEditMode} leadId={leadId} />
    </LeadFormProvider>
  );
};

export default SalesNew;
